
import { defineComponent, ref, onMounted } from "vue";
import { useStore } from "vuex";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { ErrorMessage, Field, Form } from "vee-validate";
import * as Yup from "yup";
import { Dropzone } from "dropzone";

import { hideModal } from "@/core/helpers/dom";
import ApiService from "@/core/services/ApiService";

interface Chargeback {
  merchant_id: string;
  mid_number: string;
  group_id: string;
  customer_id: string;
  dispute_date: string;
  notice_date: string;
  transaction_date: string;
  expire_date: string;
  case_number: string;
  arn: string;
  reference_id: string;
  card: {
    brand: string;
    bin: string;
    last: string;
    number: string;
  };
  reason: string;
  dispute_amount: string;
  settled_amount: string;
}

export default defineComponent({
  name: "new-chargeback-modal",
  components: {
    ErrorMessage,
    Field,
    Form,
  },
  props: {
    merchant: { type: Object, required: true },
  },
  setup(props) {
    const validationSchema = Yup.object().shape({
      case_number: Yup.string().required().label("Case Number"),
      arn: Yup.string().required().label("ARN/Reference Number"),
      card_brand: Yup.string().required().label("Card Brand"),
      card_bin: Yup.string().required().label("Card Bin"),
      card_last: Yup.string().required().label("Card Last"),
      dispute_amount: Yup.string().required().label("Dispute Amount"),
      settled_amount: Yup.string().required().label("Trans Amount"),
      reason: Yup.string().required().label("Reason Code"),
      auth_code: Yup.string().required().label("Auth Code"),
      reference_id: Yup.string().required().label("Reference ID"),
    });
    const store = useStore();
    const submitButtonRef = ref<HTMLButtonElement | null>(null);
    const newChargebackModalRef = ref<HTMLElement | null>(null);
    const dropRef = ref(null);
    const dropzone = ref<Dropzone>(null);
    const isAddedFile = ref(false);

    const chargeback = ref<Chargeback>({
      merchant_id: "",
      mid_number: "",
      group_id: "",
      customer_id: "",
      dispute_date: "",
      notice_date: "",
      transaction_date: "",
      expire_date: "",
      case_number: "",
      arn: "",
      reference_id: "",
      card: {
        brand: "",
        bin: "",
        last: "",
        number: "",
      },
      reason: "",
      dispute_amount: "",
      settled_amount: "",
    });

    onMounted(() => {
      if (dropRef.value !== null) {
        dropzone.value = new Dropzone(dropRef.value, {
          url: `${process.env.VUE_APP_API_URL}imgupload`,
          paramName: "file", // The name that will be used to transfer the file
          maxFiles: 1,
          maxFilesize: 10, // MB
          addRemoveLinks: true,
          acceptedFiles: ".png, .jpg, .jpeg, .pdf",
          autoProcessQueue: false,
        });

        dropzone.value?.on("addedfile", function () {
          isAddedFile.value = true;
        });

        dropzone.value?.on("removedfile", function () {
          isAddedFile.value = false;
        });
      }
    });

    const submit = async () => {
      if (
        !chargeback.value.dispute_date ||
        !chargeback.value.notice_date ||
        !chargeback.value.transaction_date ||
        !chargeback.value.expire_date ||
        !isAddedFile.value
      ) {
        Swal.fire({
          text: "Please input the fields!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "OK!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });

        return;
      }

      let cbnotice = "";
      try {
        // eslint-disable-next-line
        const formData: any = new FormData();
        formData.append("upload", dropzone.value.getAcceptedFiles()[0]);
        const res = await ApiService.post("imgupload", formData);

        if (!res.data.success) {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });

          return;
        }

        cbnotice = res.data.data.url;
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });

        return;
      }

      // eslint-disable-next-line
      submitButtonRef.value!.disabled = true;
      // Activate indicator
      submitButtonRef.value?.setAttribute("data-kt-indicator", "on");

      // eslint-disable-next-line
      const payload: any = { ...chargeback.value };
      payload.group_id = store.getters.currentUser.data.companygroupid;
      payload.customer_id = store.getters.currentUser.data.customer_id;
      payload.merchant_id = props.merchant.merchant_id;
      payload.mid_number = props.merchant.mid_number;
      payload.card.number = `${chargeback.value.card.bin}******${chargeback.value.card.last}`;
      payload.cbnotice = cbnotice;

      try {
        const res = await ApiService.post(
          `chargebacks/incoming?action=new_chargeback`,
          payload
        );
        if (res.data.success) {
          Swal.fire({
            text: "New Chargeback Successfully Created!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          }).then(function () {
            hideModal(newChargebackModalRef.value);
          });
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
      }

      submitButtonRef.value?.removeAttribute("data-kt-indicator");
      // eslint-disable-next-line
      submitButtonRef.value!.disabled = false;
    };

    return {
      validationSchema,
      submitButtonRef,
      newChargebackModalRef,
      submit,
      dropRef,
      chargeback,
    };
  },
});
